import moment from "moment"
import { getToday, type IsoDate } from "helpers/types/dates"
import { getStartDateWithBoundaries, type Period } from "helpers/dates"

export interface DateOverride {
  period: Period
  startDate: IsoDate
  endDate: IsoDate
}

// Intraday can roll endDate to today before latestDate's boundary metadata catches up (date-calculations.ts).
export function getEffectiveLatestDate(
  isIntraday: boolean,
  endDate: IsoDate | null,
  latestDate: IsoDate | null,
): IsoDate | null {
  const today = getToday()
  if (isIntraday && endDate === today && (latestDate == null || latestDate < today)) return today
  return latestDate
}

// Boundary-clamped only (no unmodeledDates/intraday-availability lookup) — a panel override doesn't
// have its own model-metadata fetch. Weekend rollover still applies (isUnmodeledDate treats
// isoWeekday > 5 as unmodeled regardless of the unmodeledDates list); the known gap is a model-specific
// holiday closure landing exactly on the computed start date. Anchored on latestDate (not the
// dashboard's current endDate) so a preset always means "trailing from the most recent data",
// independent of whatever the dashboard itself currently shows.
export function computeDateOverride(
  period: Period,
  earliestDate: IsoDate | null,
  latestDate: IsoDate | null,
): DateOverride | null {
  // A null latestDate doesn't make getStartDateWithBoundaries return null — it produces the string
  // "Invalid date" via moment(), so this has to be guarded explicitly before that call.
  if (earliestDate == null || latestDate == null) return null
  const startDate = getStartDateWithBoundaries(latestDate, period, earliestDate, latestDate, null, false)
  if (startDate == null) return null
  return { period, startDate, endDate: latestDate }
}

interface EffectiveDateOverride {
  startDate: IsoDate
  endDate: IsoDate
  performanceStartDate: IsoDate
}

// Fixed-length presets — a single-day window here means the range genuinely collapsed (e.g. the
// panel's boundaries shrank to one day since the override was set), never the preset's own intent.
const FIXED_LENGTH_PERIODS: Period[] = ["1M", "3M", "6M", "1Y", "3Y"]

// Re-derives startDate/endDate from period + the panel's current boundaries on every read (rather
// than clamping the persisted absolute dates), so a stored "3M" override always means "trailing 3
// months from today's latestDate" instead of freezing the window from whenever it was set.
export function resolveDateOverride(
  dateOverride: DateOverride,
  earliestDate: IsoDate | null,
  latestDate: IsoDate | null,
): EffectiveDateOverride | null {
  if (earliestDate == null || latestDate == null) return null
  const computed = computeDateOverride(dateOverride.period, earliestDate, latestDate)
  if (computed == null) return null
  if (computed.startDate === computed.endDate && FIXED_LENGTH_PERIODS.includes(dateOverride.period)) return null
  const performanceStartDate = getStartDateWithBoundaries(computed.endDate, dateOverride.period, earliestDate, latestDate, null, true) ?? computed.startDate
  return { startDate: computed.startDate, endDate: computed.endDate, performanceStartDate }
}

export function formatDateRangeLabel(startDate: IsoDate, endDate: IsoDate): string {
  const start = moment(startDate)
  const end = moment(endDate)
  const startFormat = start.year() === end.year() ? "MMM D" : "MMM D, YYYY"
  return `${start.format(startFormat)} – ${end.format("MMM D, YYYY")}`
}
